import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import {
  Rocket, Pencil, Globe, MapPin, Loader2,
  Star, Trash2, Clock,
} from 'lucide-react';
import { toast } from 'sonner';

const siteTypeLabel = { geo: 'Geographical', industry: 'Industry' };
const siteTypeBadge = {
  geo:      'bg-blue-100 text-blue-700 border border-blue-200',
  industry: 'bg-purple-100 text-purple-700 border border-purple-200',
};

const emptyForm = {
  site_type:            'geo',
  display_name:         '',
  hub_name:             '',
  tagline:              '',
  region:               '',
  industry_category:    '',
  primary_colour:       '#1F1A17',
  accent_colour:        '#C2692A',
  logo_url:             '',
  favicon_url:          '',
  default_og_image_url: '',
  twitter_url:          '',
  linkedin_url:         '',
  facebook_url:         '',
  whatsapp_url:         '',
  analytics_id:         '',
};

const STORY_PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 350;

function timeUntilExpiry(assignedAt) {
  if (!assignedAt) return null;
  const expiry = new Date(new Date(assignedAt).getTime() + 24 * 60 * 60 * 1000);
  const diffMs = expiry - new Date();
  if (diffMs <= 0) return null;
  const hours   = Math.floor(diffMs / (1000 * 60 * 60));
  const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
  return `${hours}h ${minutes}m remaining`;
}

function isSlotActive(assignedAt) {
  if (!assignedAt) return false;
  return new Date() < new Date(new Date(assignedAt).getTime() + 24 * 60 * 60 * 1000);
}


// ─── Top Story Slot ───

function TopStorySlot({
  slot,
  pub,
  storyMap,
  stories,
  storiesLoading,
  excludeIds,
  search,
  onSearch,
  picking,
  onPicking,
  onAssign,
  onClear,
  assigning,
}) {
  const slotId = pub[`top_story_slot_${slot}_id`];
  const slotAt = pub[`top_story_slot_${slot}_assigned_at`];
  const active = isSlotActive(slotAt) && !!slotId;
  const story  = active ? storyMap[slotId] : null;
  const expiry = active ? timeUntilExpiry(slotAt) : null;

  const options = stories.filter(s => !excludeIds.includes(s.id));

  return (
    <div className="border border-border rounded-lg p-3 space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-[#6E6258] uppercase tracking-wide">
          Slot {slot}
        </span>
        {active
          ? <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-xs">Manual</Badge>
          : <Badge className="bg-[#F8EBD9] text-[#6E6258] border-[#E7D2B8] text-xs">Auto</Badge>
        }
      </div>

      {active ? (
        <div className="space-y-1.5">
          <p className="text-sm font-medium text-foreground line-clamp-2">
            {story?.headline || 'Loading pinned story...'}
          </p>
          {expiry && (
            <p className="text-xs text-amber-600 flex items-center gap-1">
              <Clock size={11} /> {expiry}
            </p>
          )}
          <Button
            variant="ghost" size="sm"
            onClick={() => onClear(slot)}
            className="h-6 px-2 text-xs text-red-500 hover:text-red-600 hover:bg-red-50 gap-1"
          >
            <Trash2 size={11} /> Clear
          </Button>
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">
          Auto-fills with most recent published story
        </p>
      )}

      {!picking ? (
        <Button
          variant="outline" size="sm"
          onClick={() => onPicking(slot)}
          className="w-full h-7 text-xs border-border text-muted-foreground hover:bg-[#FFF4E8] hover:text-foreground gap-1"
        >
          <Star size={11} /> {active ? 'Change story' : 'Pin a story'}
        </Button>
      ) : (
        <div className="space-y-2">
          <Input
            value={search}
            onChange={e => onSearch(e.target.value)}
            placeholder="Search this site's published stories..."
            className="h-7 text-xs bg-white border-border"
            autoFocus
          />
          <div className="max-h-40 overflow-y-auto space-y-1">
            {storiesLoading && (
              <p className="text-xs text-muted-foreground text-center py-2 flex items-center justify-center gap-1.5">
                <Loader2 size={11} className="animate-spin" /> Loading stories...
              </p>
            )}
            {!storiesLoading && options.length === 0 && (
              <p className="text-xs text-muted-foreground text-center py-2">
                {search
                  ? 'No published stories match that search'
                  : 'This site has no published stories yet'}
              </p>
            )}
            {!storiesLoading && options.map(s => (
              <button
                key={s.id}
                onClick={() => onAssign(slot, s)}
                disabled={assigning === slot}
                className="w-full text-left px-2 py-1.5 text-xs hover:bg-[#FFF4E8] rounded border border-transparent hover:border-[#E7D2B8] transition-colors"
              >
                <span className="line-clamp-2 text-foreground">{s.headline}</span>
              </button>
            ))}
          </div>
          <Button
            variant="ghost" size="sm"
            onClick={() => onPicking(null)}
            className="w-full h-6 text-xs text-muted-foreground"
          >
            Cancel
          </Button>
        </div>
      )}
    </div>
  );
}


// ─── Subscribers List ───

function SubscribersList({ pubId }) {
  const [subs, setSubs]       = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!pubId) return;
    api.get('/newsletter-subscribers', { params: { publication_id: pubId } })
      .then(res => setSubs(res.data || []))
      .catch(() => setSubs([]))
      .finally(() => setLoading(false));
  }, [pubId]);

  if (loading) return (
    <div className="flex items-center gap-2 text-xs text-muted-foreground py-2">
      <Loader2 size={12} className="animate-spin" /> Loading subscribers...
    </div>
  );

  if (subs.length === 0) return (
    <p className="text-xs text-muted-foreground">No subscribers yet.</p>
  );

  return (
    <div className="space-y-1 max-h-48 overflow-y-auto">
      <p className="text-xs text-muted-foreground mb-2">
        {subs.length} subscriber{subs.length !== 1 ? 's' : ''}
      </p>
      {subs.map(s => (
        <div
          key={s.id}
          className="flex items-center justify-between px-2 py-1.5 bg-[#FFF4E8] rounded text-xs"
        >
          <span className="text-foreground">{s.first_name} {s.last_name}</span>
          <span className="text-muted-foreground">{s.email}</span>
        </div>
      ))}
    </div>
  );
}


// ─── Main Page ───

export default function SiteLauncherPage() {
  const { user } = useAuth();
  const [pubs, setPubs]                     = useState([]);
  const [stories, setStories]               = useState([]);
  const [storyMap, setStoryMap]             = useState({});
  const [storiesLoading, setStoriesLoading] = useState(false);
  const [loading, setLoading]               = useState(true);
  const [dialog, setDialog]                 = useState(false);
  const [editing, setEditing]               = useState(null);
  const [form, setForm]                     = useState(emptyForm);
  const [saving, setSaving]                 = useState(false);
  const [assigning, setAssigning]           = useState(null);
  const [search, setSearch]                 = useState('');
  const [storySearch, setStorySearch]       = useState('');
  const [pickingSlot, setPickingSlot]       = useState(null);
  const [activeTab, setActiveTab]           = useState('config');

  const canManage = ['admin', 'editor'].includes(user?.role);

  const fetchData = async () => {
    try {
      const res = await api.get('/publications');
      setPubs(res.data || []);
    } catch { toast.error('Failed to load publications'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const loadPubStories = async (pubId, term) => {
    setStoriesLoading(true);
    try {
      const params = {
        status:         'published',
        publication_id: pubId,
        sort:           'public',
        page_size:      STORY_PAGE_SIZE,
      };
      if (term && term.trim()) params.search = term.trim();
      const res  = await api.get('/stories', { params });
      const list = res.data?.stories || [];
      setStories(list);
      setStoryMap(prev => {
        const next = { ...prev };
        list.forEach(s => { next[s.id] = s; });
        return next;
      });
    } catch {
      setStories([]);
    } finally { setStoriesLoading(false); }
  };

  const loadSlotStories = async (pub) => {
    const ids = [1, 2, 3]
      .filter(slot => isSlotActive(pub[`top_story_slot_${slot}_assigned_at`]))
      .map(slot => pub[`top_story_slot_${slot}_id`])
      .filter(Boolean);
    if (ids.length === 0) return;
    const results = await Promise.allSettled(ids.map(id => api.get(`/stories/${id}`)));
    setStoryMap(prev => {
      const next = { ...prev };
      results.forEach(r => {
        if (r.status === 'fulfilled' && r.value.data?.id) next[r.value.data.id] = r.value.data;
      });
      return next;
    });
  };

  useEffect(() => {
    if (!dialog || activeTab !== 'topstories' || !editing?.id) return;
    const t = setTimeout(() => loadPubStories(editing.id, storySearch), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [dialog, activeTab, editing?.id, storySearch]);

  useEffect(() => {
    if (!dialog || activeTab !== 'topstories' || !editing?.id) return;
    loadSlotStories(editing);
  }, [dialog, activeTab, editing?.id]);

  const openEdit = (pub) => {
    setEditing(pub);
    setActiveTab('config');
    setStories([]);
    setStorySearch('');
    setPickingSlot(null);
    setForm({
      site_type:            pub.site_type            || 'geo',
      display_name:         pub.display_name         || '',
      hub_name:             pub.hub_name             || '',
      tagline:              pub.tagline              || '',
      region:               pub.region               || '',
      industry_category:    pub.industry_category    || '',
      primary_colour:       pub.primary_colour       || '#1F1A17',
      accent_colour:        pub.accent_colour        || '#C2692A',
      logo_url:             pub.logo_url             || '',
      favicon_url:          pub.favicon_url          || '',
      default_og_image_url: pub.default_og_image_url || '',
      twitter_url:          pub.twitter_url          || '',
      linkedin_url:         pub.linkedin_url         || '',
      facebook_url:         pub.facebook_url         || '',
      whatsapp_url:         pub.whatsapp_url         || '',
      analytics_id:         pub.analytics_id         || '',
    });
    setDialog(true);
  };

  const handleSave = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      await api.put(`/publications/${editing.id}`, form);
      toast.success('Site config saved');
      setDialog(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const handleAssignSlot = async (slot, story) => {
    if (!editing) return;
    setAssigning(slot);
    try {
      const res = await api.put(
        `/publications/${editing.id}/top-stories/${slot}`,
        { story_id: story.id }
      );
      setStoryMap(prev => ({ ...prev, [story.id]: story }));
      setEditing(res.data);
      setPubs(prev => prev.map(p => p.id === editing.id ? res.data : p));
      setPickingSlot(null);
      setStorySearch('');
      toast.success(`Slot ${slot} pinned`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to assign slot');
    } finally { setAssigning(null); }
  };

  const handleClearSlot = async (slot) => {
    if (!editing) return;
    try {
      const res = await api.delete(`/publications/${editing.id}/top-stories/${slot}`);
      setEditing(res.data);
      setPubs(prev => prev.map(p => p.id === editing.id ? res.data : p));
      toast.success(`Slot ${slot} cleared`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to clear slot');
    }
  };

  const pinnedElsewhere = (slot) => [1, 2, 3]
    .filter(s => s !== slot)
    .map(s => editing?.[`top_story_slot_${s}_id`])
    .filter(Boolean);

  const f     = (key, val) => setForm(prev => ({ ...prev, [key]: val }));
  const label = 'text-xs text-[#6E6258] font-medium mb-1 block';

  const filtered = pubs.filter(p =>
    p.name.toLowerCase().includes(search.toLowerCase()) ||
    (p.domain || '').toLowerCase().includes(search.toLowerCase())
  );

  const tabs = [
    { key: 'config',      label: 'Config'      },
    { key: 'branding',    label: 'Branding'    },
    { key: 'social',      label: 'Social'      },
    { key: 'topstories',  label: 'Top Stories' },
    { key: 'subscribers', label: 'Subscribers' },
  ];

  const isIndustry = form.site_type === 'industry';

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground font-outfit tracking-tight">
            Site Launcher
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Configure branding and launch settings for each publication site.
          </p>
        </div>
      </div>

      <Input
        value={search}
        onChange={e => setSearch(e.target.value)}
        placeholder="Search sites..."
        className="max-w-sm bg-white text-[#1F1A17] border-border placeholder:text-[#9C8E84]"
      />

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-36 bg-[#FFF4E8] border border-border rounded-xl animate-pulse" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <Card className="bg-card border-border">
          <CardContent className="py-12 text-center">
            <Rocket className="mx-auto text-[#E7D2B8] mb-3" size={48} />
            <p className="text-muted-foreground text-lg">No publications found</p>
            <p className="text-sm text-muted-foreground mt-1">
              Create publications first from the Publications page.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map(pub => {
            const isPubIndustry = pub.site_type === 'industry';
            return (
              <Card
                key={pub.id}
                className="bg-card border-border hover:border-[#C9B49A] hover:shadow-sm transition-all"
              >
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <CardTitle className="text-base text-foreground font-outfit truncate">
                        {pub.display_name || pub.name}
                      </CardTitle>
                      {pub.display_name && pub.display_name !== pub.name && (
                        <p className="text-xs text-muted-foreground truncate">{pub.name}</p>
                      )}
                    </div>
                    {canManage && (
                      <Button
                        variant="ghost" size="icon"
                        className="h-7 w-7 text-muted-foreground hover:text-blue-600 hover:bg-[#FFF4E8] shrink-0"
                        onClick={() => openEdit(pub)}
                      >
                        <Pencil size={14} />
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="space-y-2">
                  {pub.domain && (
                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                      <Globe size={11} /> {pub.domain}
                    </p>
                  )}
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge className={`text-xs ${siteTypeBadge[pub.site_type] || siteTypeBadge.geo}`}>
                      {siteTypeLabel[pub.site_type] || 'Geo'}
                    </Badge>
                    {isPubIndustry && pub.industry_category && (
                      <span className="text-xs text-muted-foreground">
                        {pub.industry_category}
                      </span>
                    )}
                    {!isPubIndustry && pub.region && (
                      <span className="text-xs text-muted-foreground flex items-center gap-1">
                        <MapPin size={10} /> {pub.region}
                      </span>
                    )}
                  </div>
                  {pub.tagline && (
                    <p className="text-xs text-muted-foreground italic truncate">{pub.tagline}</p>
                  )}

                  <div className="flex items-center gap-1.5 mt-1">
                    <span
                      className="w-4 h-4 rounded-full border border-border shrink-0"
                      style={{ backgroundColor: pub.primary_colour || '#1F1A17' }}
                      title="Primary colour"
                    />
                    <span
                      className="w-4 h-4 rounded-full border border-border shrink-0"
                      style={{ backgroundColor: pub.accent_colour || '#C2692A' }}
                      title="Accent colour"
                    />
                    {pub.logo_url    && <span className="text-xs text-emerald-600 ml-1">Logo</span>}
                    {pub.favicon_url && <span className="text-xs text-emerald-600">Favicon</span>}
                  </div>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {pub.twitter_url  && <span className="text-[10px] text-muted-foreground bg-[#F8EBD9] px-1.5 py-0.5 rounded">X</span>}
                    {pub.linkedin_url && <span className="text-[10px] text-muted-foreground bg-[#F8EBD9] px-1.5 py-0.5 rounded">LI</span>}
                    {pub.facebook_url && <span className="text-[10px] text-muted-foreground bg-[#F8EBD9] px-1.5 py-0.5 rounded">FB</span>}
                    {pub.whatsapp_url && <span className="text-[10px] text-muted-foreground bg-[#F8EBD9] px-1.5 py-0.5 rounded">WA</span>}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* ─── Edit Dialog ─── */}
      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="bg-card border-border max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-foreground font-outfit">
              {editing?.display_name || editing?.name}
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Configure branding and launch settings. Content config is managed in Publications.
            </DialogDescription>
          </DialogHeader>

          {/* Tabs */}
          <div className="flex gap-1 border-b border-border flex-wrap">
            {tabs.map(t => (
              <button
                key={t.key}
                onClick={() => setActiveTab(t.key)}
                className={`px-3 py-1.5 text-xs font-medium rounded-t transition-colors ${
                  activeTab === t.key
                    ? 'bg-[#FFE4C4] text-[#1F1A17] border border-b-0 border-border'
                    : 'text-muted-foreground hover:text-foreground hover:bg-[#FFF4E8]'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          <div className="space-y-4 pt-2">

            {/* Config tab */}
            {activeTab === 'config' && (
              <div className="space-y-3">
                <div>
                  <label className={label}>Display Name</label>
                  <Input
                    value={form.display_name}
                    onChange={e => f('display_name', e.target.value)}
                    placeholder="e.g. Reading Dispatch"
                    className="bg-white text-[#1F1A17] border-border placeholder:text-[#9C8E84]"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Public-facing name shown on the site. Defaults to publication name if empty.
                  </p>
                </div>

                {/* Site type — read only */}
                <div>
                  <label className={label}>Site Type</label>
                  <div className="flex items-center gap-2 px-3 py-2 bg-[#F8EBD9] rounded-lg">
                    <Badge className={`text-xs ${siteTypeBadge[form.site_type] || siteTypeBadge.geo}`}>
                      {siteTypeLabel[form.site_type] || 'Geo'}
                    </Badge>
                    <p className="text-xs text-muted-foreground">
                      Set in Publications. Change there if needed.
                    </p>
                  </div>
                </div>

                {/* Hub name — geo only */}
                {!isIndustry && (
                  <div>
                    <label className={label}>Hub Name</label>
                    <Input
                      value={form.hub_name}
                      onChange={e => f('hub_name', e.target.value)}
                      placeholder="e.g. Reading"
                      className="bg-white text-[#1F1A17] border-border placeholder:text-[#9C8E84]"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      The primary city this publication covers.
                    </p>
                  </div>
                )}

                {!isIndustry ? (
                  <div>
                    <label className={label}>Region</label>
                    <Input
                      value={form.region}
                      onChange={e => f('region', e.target.value)}
                      placeholder="e.g. South East"
                      className="bg-white text-[#1F1A17] border-border placeholder:text-[#9C8E84]"
                    />
                  </div>
                ) : (
                  <div>
                    <label className={label}>Industry / Sector</label>
                    <div className="flex items-center px-3 py-2 bg-[#F8EBD9] rounded-lg">
                      <p className="text-sm text-foreground flex-1">
                        {editing?.industry_category || '—'}
                      </p>
                      <p className="text-xs text-muted-foreground">Set in Publications.</p>
                    </div>
                  </div>
                )}

                <div>
                  <label className={label}>Tagline</label>
                  <Input
                    value={form.tagline}
                    onChange={e => f('tagline', e.target.value)}
                    placeholder={
                      isIndustry
                        ? 'e.g. Intelligence for finance professionals'
                        : 'e.g. Local news for Reading & Berkshire'
                    }
                    className="bg-white text-[#1F1A17] border-border placeholder:text-[#9C8E84]"
                  />
                </div>

                <div>
                  <label className={label}>Analytics ID</label>
                  <Input
                    value={form.analytics_id}
                    onChange={e => f('analytics_id', e.target.value)}
                    placeholder="e.g. G-XXXXXXXXXX"
                    className="bg-white text-[#1F1A17] border-border placeholder:text-[#9C8E84]"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Added post-launch — leave blank for now.
                  </p>
                </div>
              </div>
            )}

            {/* Branding tab */}
            {activeTab === 'branding' && (
              <div className="space-y-4">
                <div className="flex gap-4">
                  <div className="flex-1">
                    <label className={label}>Primary Colour</label>
                    <div className="flex gap-2 items-center">
                      <input
                        type="color"
                        value={form.primary_colour}
                        onChange={e => f('primary_colour', e.target.value)}
                        className="w-9 h-9 rounded border border-border cursor-pointer bg-white"
                      />
                      <Input
                        value={form.primary_colour}
                        onChange={e => f('primary_colour', e.target.value)}
                        className="bg-white text-[#1F1A17] border-border font-mono text-sm"
                      />
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">Navbar & footer</p>
                  </div>
                  <div className="flex-1">
                    <label className={label}>Accent Colour</label>
                    <div className="flex gap-2 items-center">
                      <input
                        type="color"
                        value={form.accent_colour}
                        onChange={e => f('accent_colour', e.target.value)}
                        className="w-9 h-9 rounded border border-border cursor-pointer bg-white"
                      />
                      <Input
                        value={form.accent_colour}
                        onChange={e => f('accent_colour', e.target.value)}
                        className="bg-white text-[#1F1A17] border-border font-mono text-sm"
                      />
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">Links & buttons</p>
                  </div>
                </div>

                {/* Colour preview */}
                <div className="rounded-md overflow-hidden border border-border">
                  <div
                    className="px-3 py-2 flex items-center gap-2"
                    style={{ backgroundColor: form.primary_colour }}
                  >
                    <span className="text-white text-xs font-medium truncate">
                      {form.display_name || editing?.name || 'Publication Name'}
                    </span>
                    <span
                      className="ml-auto text-xs px-2 py-0.5 rounded shrink-0"
                      style={{ backgroundColor: form.accent_colour, color: '#fff' }}
                    >
                      Subscribe
                    </span>
                  </div>
                  <div className="bg-[#FAF6F1] px-3 py-1.5">
                    <span className="text-xs" style={{ color: form.accent_colour }}>
                      Sample link colour
                    </span>
                  </div>
                </div>

                <div>
                  <label className={label}>Logo URL</label>
                  <Input
                    value={form.logo_url}
                    onChange={e => f('logo_url', e.target.value)}
                    placeholder="Upload to media library → copy URL → paste here"
                    className="bg-white text-[#1F1A17] border-border placeholder:text-[#9C8E84]"
                  />
                </div>

                <div>
                  <label className={label}>Favicon URL</label>
                  <Input
                    value={form.favicon_url}
                    onChange={e => f('favicon_url', e.target.value)}
                    placeholder="Upload to media library → copy URL → paste here"
                    className="bg-white text-[#1F1A17] border-border placeholder:text-[#9C8E84]"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Appears in browser tab. Use a square image, min 32×32px.
                  </p>
                </div>

                <div>
                  <label className={label}>Default OG Image URL</label>
                  <Input
                    value={form.default_og_image_url}
                    onChange={e => f('default_og_image_url', e.target.value)}
                    placeholder="Upload to media library → copy URL → paste here"
                    className="bg-white text-[#1F1A17] border-border placeholder:text-[#9C8E84]"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Fallback image for link previews on X, LinkedIn, WhatsApp. Use 1200×630px.
                  </p>
                </div>
              </div>
            )}

            {/* Social tab */}
            {activeTab === 'social' && (
              <div className="space-y-3">
                <p className="text-xs text-muted-foreground">
                  This site's social media profile URLs. Used in footer links and structured data.
                </p>
                <div>
                  <label className={label}>X (Twitter)</label>
                  <Input
                    value={form.twitter_url}
                    onChange={e => f('twitter_url', e.target.value)}
                    placeholder="https://x.com/readingdispatch"
                    className="bg-white text-[#1F1A17] border-border placeholder:text-[#9C8E84]"
                  />
                </div>
                <div>
                  <label className={label}>LinkedIn</label>
                  <Input
                    value={form.linkedin_url}
                    onChange={e => f('linkedin_url', e.target.value)}
                    placeholder="https://linkedin.com/company/readingdispatch"
                    className="bg-white text-[#1F1A17] border-border placeholder:text-[#9C8E84]"
                  />
                </div>
                <div>
                  <label className={label}>Facebook</label>
                  <Input
                    value={form.facebook_url}
                    onChange={e => f('facebook_url', e.target.value)}
                    placeholder="https://facebook.com/readingdispatch"
                    className="bg-white text-[#1F1A17] border-border placeholder:text-[#9C8E84]"
                  />
                </div>
                <div>
                  <label className={label}>WhatsApp</label>
                  <Input
                    value={form.whatsapp_url}
                    onChange={e => f('whatsapp_url', e.target.value)}
                    placeholder="https://wa.me/447700000000"
                    className="bg-white text-[#1F1A17] border-border placeholder:text-[#9C8E84]"
                  />
                </div>
              </div>
            )}

            {/* Top Stories tab */}
            {activeTab === 'topstories' && (
              <div className="space-y-3">
                <p className="text-xs text-muted-foreground">
                  Pin a published story from this site to each slot. A pinned story always appears
                  in Top Stories, even if it would otherwise have been the lead story. Stays for
                  24 hours then reverts to auto. Empty slots auto-fill with most recent published stories.
                </p>
                {[1, 2, 3].map(slot => (
                  <TopStorySlot
                    key={slot}
                    slot={slot}
                    pub={editing || {}}
                    storyMap={storyMap}
                    stories={stories}
                    storiesLoading={storiesLoading}
                    excludeIds={pinnedElsewhere(slot)}
                    search={storySearch}
                    onSearch={setStorySearch}
                    picking={pickingSlot === slot}
                    onPicking={(s) => { setPickingSlot(s); setStorySearch(''); }}
                    onAssign={handleAssignSlot}
                    onClear={handleClearSlot}
                    assigning={assigning}
                  />
                ))}
              </div>
            )}

            {/* Subscribers tab */}
            {activeTab === 'subscribers' && (
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground">
                  Subscribers collected via the newsletter form on this site.
                </p>
                {editing && <SubscribersList pubId={editing.id} />}
              </div>
            )}
          </div>

          {activeTab !== 'subscribers' && activeTab !== 'topstories' && (
            <DialogFooter>
              <Button
                variant="ghost"
                onClick={() => setDialog(false)}
                className="text-muted-foreground hover:text-foreground"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-[#1F1A17] hover:bg-[#2B241F] text-[#FFE4C4]"
              >
                {saving
                  ? <><Loader2 size={14} className="animate-spin mr-1.5" />Saving...</>
                  : 'Save Config'
                }
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}